import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

// ─── helpers ──────────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem('token');
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

const PERIOD_LABEL = { daily: 'Today', weekly: 'This Week', monthly: 'This Month' };

function daysLeftInPeriod(period) {
  const now = new Date();
  if (period === 'daily') return 0;
  if (period === 'weekly') {
    const day = now.getDay(); // 0=Sun
    return day === 0 ? 0 : 7 - day;
  }
  // monthly
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return lastDay - now.getDate();
}

function pct(current, target) {
  if (!target || target <= 0) return 0;
  return Math.min(100, Math.round((current / target) * 100));
}

// ─── Confetti / Stars Overlay ─────────────────────────────────────────────────
const CONFETTI_COLORS = ['#FFD600', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96E6A1', '#DDA0DD', '#FFA07A'];
const STAR_COUNT = 18;
const CONFETTI_COUNT = 40;

function CelebrationOverlay({ target, onDismiss }) {
  const [counter, setCounter] = useState(0);
  const goal = Math.round(target.target_value);

  // roll counter up
  useEffect(() => {
    let frame;
    let start = null;
    const duration = 1400;
    const animate = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setCounter(Math.round(progress * goal));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [goal]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)' }}
      onClick={onDismiss}
    >
      {/* Floating stars */}
      {Array.from({ length: STAR_COUNT }).map((_, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            left: `${5 + (i * 5.5) % 90}%`,
            bottom: '-10px',
            fontSize: `${14 + (i % 4) * 8}px`,
            animation: `starFloat ${1.4 + (i % 5) * 0.3}s ease-out ${(i % 7) * 0.12}s forwards`,
            opacity: 0,
          }}
        >
          {i % 3 === 0 ? '⭐' : i % 3 === 1 ? '✨' : '🌟'}
        </span>
      ))}

      {/* Confetti dots */}
      {Array.from({ length: CONFETTI_COUNT }).map((_, i) => (
        <span
          key={`c${i}`}
          style={{
            position: 'absolute',
            left: `${(i * 2.5) % 100}%`,
            top: '-8px',
            width: 10,
            height: 10,
            borderRadius: i % 2 === 0 ? '50%' : '2px',
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animation: `confettiFall ${1.2 + (i % 6) * 0.25}s ease-in ${(i % 9) * 0.09}s forwards`,
            opacity: 0,
          }}
        />
      ))}

      {/* Main card */}
      <div
        style={{
          background: 'linear-gradient(135deg,#1a1a1a 0%,#2d2200 100%)',
          border: '3px solid #FFD600',
          borderRadius: 28,
          padding: '40px 48px',
          textAlign: 'center',
          maxWidth: 420,
          width: '90vw',
          animation: 'cardPop 0.5s cubic-bezier(0.34,1.56,0.64,1) both',
          position: 'relative',
          zIndex: 2,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 64, marginBottom: 8, animation: 'trophyBounce 0.8s ease 0.4s both' }}>🏆</div>
        <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: '3px', color: '#FFD600', textTransform: 'uppercase', marginBottom: 8 }}>
          TARGET CRUSHED!
        </div>
        <div style={{ fontSize: 42, fontWeight: 900, color: '#fff', lineHeight: 1, marginBottom: 4 }}>
          {counter} <span style={{ fontSize: 20, color: '#FFD600' }}>/ {goal}</span>
        </div>
        <div style={{ fontSize: 15, color: '#ccc', marginBottom: 6 }}>{target.metric}</div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 28 }}>
          {PERIOD_LABEL[target.period] || target.period} · {target.role_or_user_id}
        </div>

        {/* Stars row */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 28 }}>
          {[0, 1, 2, 3, 4].map((s) => (
            <span key={s} style={{ fontSize: 28, animation: `starPop 0.3s ease ${0.6 + s * 0.12}s both`, opacity: 0 }}>⭐</span>
          ))}
        </div>

        <div style={{ fontSize: 11, color: '#888', marginBottom: 20 }}>Amazing work! Keep it up 💛</div>
        <button
          onClick={onDismiss}
          style={{
            background: '#FFD600', color: '#111', fontWeight: 900, fontSize: 14,
            border: 'none', borderRadius: 999, padding: '12px 36px', cursor: 'pointer',
            letterSpacing: '1px',
          }}
        >
          Continue 🚀
        </button>
      </div>

      <style>{`
        @keyframes starFloat {
          0%   { transform: translateY(0) scale(0.5) rotate(-20deg); opacity: 0; }
          20%  { opacity: 1; }
          100% { transform: translateY(-110vh) scale(1.2) rotate(30deg); opacity: 0; }
        }
        @keyframes confettiFall {
          0%   { transform: translateY(-10px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(105vh) rotate(720deg); opacity: 0.3; }
        }
        @keyframes cardPop {
          from { transform: scale(0.4); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
        @keyframes trophyBounce {
          0%   { transform: scale(0) rotate(-30deg); opacity: 0; }
          70%  { transform: scale(1.3) rotate(10deg); opacity: 1; }
          100% { transform: scale(1)   rotate(0deg);  opacity: 1; }
        }
        @keyframes starPop {
          from { transform: scale(0); opacity: 0; }
          to   { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ─── Single target card ───────────────────────────────────────────────────────
function TargetCard({ t, onCelebrate }) {
  const pctVal = pct(t.current_value, t.target_value);
  const daysLeft = daysLeftInPeriod(t.period);
  const missed = pctVal < 100 && daysLeft === 0 && t.period !== 'daily';
  const warning2days = !missed && pctVal < 100 && daysLeft <= 2;
  const achieved = pctVal >= 100;
  const celebratedRef = useRef(false);

  useEffect(() => {
    if (achieved && !celebratedRef.current) {
      celebratedRef.current = true;
      onCelebrate(t);
    }
  }, [achieved, onCelebrate, t]);

  const barColor = missed
    ? 'bg-red-500'
    : warning2days
    ? 'bg-yellow-400'
    : achieved
    ? 'bg-emerald-500'
    : 'bg-blue-500';

  const cardBorder = missed
    ? 'border-red-300 bg-red-50'
    : warning2days
    ? 'border-yellow-300 bg-yellow-50'
    : achieved
    ? 'border-emerald-300 bg-emerald-50'
    : 'border-gray-200 bg-white';

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${cardBorder} transition-all`}>
      {/* header row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-black uppercase tracking-widest text-gray-500 truncate">
            {PERIOD_LABEL[t.period] || t.period}
          </p>
          <p className="text-sm font-extrabold text-gray-900 mt-0.5 leading-tight">{t.metric}</p>
          {t.description && (
            <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">{t.description}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          {achieved && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-300 px-2.5 py-1 text-[11px] font-extrabold text-emerald-700">
              ✅ Achieved
            </span>
          )}
          {missed && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 border border-red-300 px-2.5 py-1 text-[11px] font-extrabold text-red-600">
              ⚠ Missed
            </span>
          )}
          {warning2days && !achieved && !missed && (
            <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 border border-yellow-300 px-2.5 py-1 text-[11px] font-extrabold text-yellow-700 animate-pulse">
              ⚡ {daysLeft}d left
            </span>
          )}
        </div>
      </div>

      {/* counter */}
      <div className="flex items-end gap-1 mb-2">
        <span className={`text-3xl font-black leading-none ${achieved ? 'text-emerald-700' : missed ? 'text-red-600' : 'text-gray-900'}`}>
          {t.current_value}
        </span>
        <span className="text-base font-semibold text-gray-400 mb-0.5">/ {t.target_value}</span>
        {t.metric_unit === 'amount' && <span className="text-xs text-gray-400 mb-0.5">₹</span>}
        {t.metric_unit === 'percent' && <span className="text-xs text-gray-400 mb-0.5">%</span>}
        <span className="ml-auto text-sm font-black text-gray-500">{pctVal}%</span>
      </div>

      {/* progress bar */}
      <div className="h-3 w-full rounded-full bg-gray-200 overflow-hidden">
        <div
          className={`h-3 rounded-full transition-all duration-700 ${barColor}`}
          style={{ width: `${pctVal}%` }}
        />
      </div>

      {/* 2-day warning message */}
      {warning2days && (
        <div className="mt-2.5 rounded-xl bg-yellow-100 border border-yellow-300 px-3 py-2 flex items-center gap-2">
          <span className="text-lg">⚡</span>
          <p className="text-xs font-bold text-yellow-800">
            Only <span className="text-yellow-900 font-extrabold">{daysLeft} day{daysLeft !== 1 ? 's' : ''}</span> left!
            {' '}Need <span className="font-extrabold">{Math.max(0, t.target_value - t.current_value)} more</span> to hit target.
          </p>
        </div>
      )}

      {/* missed warning */}
      {missed && (
        <div className="mt-2.5 rounded-xl bg-red-100 border border-red-300 px-3 py-2 flex items-center gap-2">
          <span className="text-lg">🚨</span>
          <p className="text-xs font-bold text-red-700">
            Target missed! Fell short by <span className="font-extrabold">{Math.max(0, t.target_value - t.current_value)}</span>.
            {' '}Keep going next period!
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Main exported component ──────────────────────────────────────────────────
const TargetWidget = ({ API_URL }) => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);        // null | string
  const [celebTarget, setCelebTarget] = useState(null);
  const [dismissed, setDismissed] = useState({}); // {targetId: true} — don't re-show after dismiss

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      if (!token) { setTargets([]); setError('Not logged in'); return; }
      const res = await axios.get(`${API_URL}/api/staff/my-targets`, { headers: authHeader() });
      setTargets(res.data || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError('Auth error (' + status + ') — try logging out and back in');
      } else if (!err?.response) {
        setError('Cannot reach server — check backend is running');
      } else {
        setError('Failed to load targets (' + (status || err.message) + ')');
      }
      setTargets([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  // refresh every 5 minutes
  useEffect(() => {
    const t = setInterval(fetchTargets, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchTargets]);

  const handleCelebrate = useCallback((t) => {
    if (!dismissed[t.id]) setCelebTarget(t);
  }, [dismissed]);

  const handleDismiss = () => {
    if (celebTarget) setDismissed((d) => ({ ...d, [celebTarget.id]: true }));
    setCelebTarget(null);
  };

  return (
    <>
      {celebTarget && !dismissed[celebTarget.id] && (
        <CelebrationOverlay target={celebTarget} onDismiss={handleDismiss} />
      )}

      <div className="mb-5 rounded-2xl border border-yellow-200 bg-yellow-50/50 px-4 py-4">
        {/* section header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">🎯</span>
            <span className="text-sm font-extrabold uppercase tracking-widest text-gray-700">My Targets</span>
          </div>
          {!loading && (
            <button
              type="button"
              onClick={fetchTargets}
              className="rounded-xl border border-yellow-300 px-3 py-1 text-[11px] font-bold text-yellow-700 hover:bg-yellow-100 transition"
            >
              ↻ Refresh
            </button>
          )}
        </div>

        {/* loading */}
        {loading && (
          <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-yellow-400 border-t-transparent" />
            Loading your targets…
          </div>
        )}

        {/* error */}
        {!loading && error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3">
            <span className="text-lg mt-0.5">⚠️</span>
            <div className="flex-1">
              <p className="text-sm font-bold text-red-700">{error}</p>
            </div>
            <button type="button" onClick={fetchTargets} className="rounded-lg bg-red-100 border border-red-300 px-3 py-1 text-xs font-bold text-red-700 hover:bg-red-200 shrink-0">
              Retry
            </button>
          </div>
        )}

        {/* no targets */}
        {!loading && !error && targets.length === 0 && (
          <div className="rounded-xl border border-dashed border-yellow-300 bg-white px-4 py-5 text-center">
            <p className="text-2xl mb-1">🏁</p>
            <p className="text-sm font-bold text-gray-700">No targets assigned yet</p>
            <p className="text-xs text-gray-400 mt-0.5">Your manager will set your targets here soon.</p>
          </div>
        )}

        {/* targets grid */}
        {!loading && !error && targets.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {targets.map((t) => (
              <TargetCard key={t.id} t={t} onCelebrate={handleCelebrate} />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default TargetWidget;
